<template>
  <v-row class="mt-2">
    <v-col>
      <ValidationObserver ref="session">
        <v-expansion-panels multiple>
          <v-expansion-panel v-test="{ panel: 'tradeSession' }">
            <ValidationObserver ref="gavel" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="gavel">
                  <v-icon>
                    gavel
                  </v-icon>
                  {{ $t('Торговая сессия (Открытая)') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row v-if="sessionInfo.sessionNumber" class="pt-3">
                  <v-col
                      cols="12"
                      class="text-left pt-0 pl-1">
                    <text-view
                        :display="sessionInfo.sessionNumber"
                        :label="$t('Номер сессии')"
                        :no-data="$t('Не присвоен')" />
                  </v-col>
                </v-row>
                <v-row class="pt-3">
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-4 pr-6 py-0">
                    <validatable-date-time-picker
                        v-model="session.dateStart"
                        v-test="{ date: 'dateStart' }"
                        :readonly="readonly"
                        :rules="{ required: true,
                                  sessionStartTimeMin: true }"
                        :day-off-dates="dayOffDates"
                        :label="$t('Время начала торговой сессии')" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-4 pr-6 py-0">
                    <validatable-date-time-picker
                        v-model="session.dateEnd"
                        v-test="{ date: 'dateEnd' }"
                        :readonly="readonly"
                        :rules="{ required: true,
                                  sessionEndDate: {
                                    startDate: session.dateStart
                                  },
                                  sessionEndTimeMax: {
                                    prolongation: session.prolongation
                                  } }"
                        :day-off-dates="dayOffDates"
                        :label="$t('Время завершения торговой сессии')" />
                  </v-col>
                </v-row>
                <v-row class="pt-3">
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 py-0">
                    <validatable-enum-combobox
                        v-model="session.tradeDirectionType"
                        v-test="{ combobox: 'tradeDirectionType' }"
                        dense
                        :label="$t('Направление торгов')"
                        readonly
                        rules="required"
                        :values-enum="TRADE_DIRECTION_TYPE_ENUM" />
                  </v-col>
                  <v-col
                      cols="12"
                      md="6"
                      class="pl-2 py-0">
                    <validatable-text-input
                        v-model="session.prolongation"
                        v-test="{ input: 'prolongation' }"
                        :readonly="readonly"
                        :rules="{ required: true,
                                  regex: /^\d{1,11}$/,
                                  min_value: 0 }"
                        dense
                        :label="$t('Продление торговой сессии, мин.')"
                        data-type="int" />
                  </v-col>
                </v-row>
                <v-row>
                  <v-col
                      cols="12"
                      md="12"
                      class="pl-2 py-0">
                    <v-data-table
                        :headers="headers1"
                        :items="session.startPremiums"
                        :items-per-page="itemsPerPage"
                        item-key="positionId"
                        class="elevation-1"
                        disable-sort-by
                        dense
                        hide-default-footer
                        :locale="systemLocale">
                      <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                      <template v-slot:item.positionDescription="{ item }">
                        {{ item.positionDescription }}
                      </template>
                      <!-- eslint-disable-next-line  vue/no-template-shadow, vue/valid-v-slot -->
                      <template v-slot:item.premium="{ item }">
                        <v-row class="pb-1 pt-0 my-0">
                          <v-col class="py-0 my-0">
                            <validatable-text-input
                                :key="item.positionId"
                                v-model="item.premium"
                                v-test="{ input: 'premiumStart' }"
                                :readonly="readonly"
                                :rules="{ required: true,
                                          max_value: isFixPremium
                                            ? MAX_FIX_PREMIUM_VALUE
                                            : MAX_DIFF_PREMIUM_VALUE,
                                          min_value: isFixPremium
                                            ? MIN_FIX_PREMIUM_VALUE
                                            : MIN_DIFF_PREMIUM_VALUE,
                                          regex: isFixPremium
                                            ? /^[0-9]{1,11}([.][0-9]{0,2})?$/
                                            : /^([-])?[0-9]{1,3}([.][0-9]{0,2})?$/ }"
                                hide-details="auto"
                                :tabindex="1"
                                solo
                                dense
                                :label="premiumLabel"
                                data-type="float" />
                          </v-col>
                        </v-row>
                      </template>
                    </v-data-table>
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-if="isAdmin || isLotOwner(lot)" v-test="{ panel: 'invitedOrganizations' }">
            <ValidationObserver ref="invitedOrganizations" v-slot="{ failed }">
              <v-expansion-panel-header>
                <div id="invitedOrganizations">
                  <v-icon>
                    switch_account
                  </v-icon>
                  {{ $t('Приглашение участников') }}
                  <v-icon v-if="failed" color="error">
                    warning
                  </v-icon>
                </div>
              </v-expansion-panel-header>
              <v-expansion-panel-content eager>
                <v-row>
                  <v-col
                      cols="12"
                      class="pl-2 py-0">
                    <v-checkbox
                        v-model="showOrganizations"
                        v-test="{ checkbox: 'showOrganizations' }"
                        :disabled="readonly"
                        :label="$t('«Закрытые торги по составу участников»/«Ограничить участников»')" />
                  </v-col>
                  <v-col
                      v-if="showOrganizations"
                      cols="12"
                      class="pl-2 py-0">
                    <validatable-invited-organizations
                        v-model="session.invitedOrganizations"
                        v-test="{ combobox: 'invitedOrganizations' }"
                        :label="$t('Приглашенные организации')"
                        :search-label="$t('Выбор организации')"
                        :readonly="readonly"
                        :params="invitedOrganizationsParams" />
                  </v-col>
                </v-row>
              </v-expansion-panel-content>
            </ValidationObserver>
          </v-expansion-panel>

          <v-expansion-panel v-if="attachments">
            <v-expansion-panel-header>
              <div id="attach_file">
                <v-icon>
                  attach_file
                </v-icon>
                {{ $t('Документы по торговой процедуре') }}
              </div>
            </v-expansion-panel-header>
            <v-expansion-panel-content eager>
              <!-- <v-card> -->
              <slot name="attachments" />
              <!-- </v-card> -->
            </v-expansion-panel-content>
          </v-expansion-panel>

        </v-expansion-panels>
      </ValidationObserver>
    </v-col>
  </v-row>
</template>

<style scoped>

  .positions::v-deep .header-size, .positions::v-deep .cell-size {
    min-width: 220px!important;
  }
  .positions::v-deep .cell-size {
    padding-top: 10px!important;
  }
</style>

<script>
  import Vue from 'vue'
  import ValidatableTextInput from '@/components/reused/ui/ValidatableTextInput.vue'
  import ValidatableEnumCombobox from '@/components/reused/ui/ValidatableEnumCombobox.vue'
  import ValidatableDateTimePicker from '@/components/reused/ui/ValidatableDateTimePicker.vue'
  import ValidatableInvitedOrganizations from '@/components/reused/ui/ValidatableInvitedOrganizations.vue'
  import TextView from '@/components/reused/ui/TextView.vue'
  import { TRADE_DIRECTION_TYPE_ENUM } from '@/constants/enums'
  import {
    PARTICIPANT_TYPES, TRADE_DIRECTION_TYPES, PREMIUM_TYPES,
    MAX_FIX_PREMIUM_VALUE, MIN_FIX_PREMIUM_VALUE,
    MAX_DIFF_PREMIUM_VALUE, MIN_DIFF_PREMIUM_VALUE
  } from '@/constants/business'
  import { mapState } from 'vuex'
  import { CALENDAR } from '@/constants/store.js'
  export default {
    components: {
      ValidatableTextInput,
      ValidatableEnumCombobox,
      ValidatableInvitedOrganizations,
      ValidatableDateTimePicker,
      TextView
    },
    props: {
      readonly: {
        type: Boolean,
        default: false
      },
      sessionInfo: {
        type: Object,
        default: () => {
          return {}
        }
      },
      lot: {
        type: Object
      },
      create: {
        type: Boolean,
        default: false
      },
      validated: {
        type: [Function, Promise]
      },
      attachments: {
        type: Boolean,
        default: false
      }
    },
    data () {
      return {
        isLoading: false,
        TRADE_DIRECTION_TYPE_ENUM,
        MAX_FIX_PREMIUM_VALUE,
        MIN_FIX_PREMIUM_VALUE,
        MAX_DIFF_PREMIUM_VALUE,
        MIN_DIFF_PREMIUM_VALUE,
        itemsPerPage: -1,
        invitedOrganizationsParams: {
          participantType: PARTICIPANT_TYPES.ORGPART
        },
        showOrganizations: false,
        session: {
          dateStart: null,
          dateEnd: null,
          tradeDirectionType: TRADE_DIRECTION_TYPES.RAISING,
          prolongation: null,
          startPremiums: [],
          invitedOrganizations: []
        }
      }
    },
    computed: {
      ...mapState(CALENDAR.NAMESPACE, { dayOffDays: CALENDAR.STATE.DAY_OFF_DAYS }),
      isFixPremium () {
        return this.lot.premiumType === PREMIUM_TYPES.FIX
      },
      headers1 () {
        return [
          { text: this.$t('Предмет договора'), sortable: false, value: 'positionDescription' },
          { text: this.premiumLabel, sortable: false, value: 'premium' }
        ]
      },
      premiumLabel () {
        return this.isFixPremium
          ? this.$t('Начальная цена 1 (одной) углеродной единицы (с учетом НДС)')
          : this.$t('Стартовое значение дифференциала')
      },
      dayOffDates () {
        const years = this.dayOffDays
        const now = this.$moment()
        const thisYear = this.$moment().format('YYYY')
        const nextYear = now.add(1, 'years').format('YYYY')
        const thisYearDayOffDates = years[thisYear]
        const nextYearDayOffDates = years[nextYear]
        if (thisYearDayOffDates && nextYearDayOffDates) {
          return thisYearDayOffDates.concat(nextYearDayOffDates).map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        if (thisYearDayOffDates) {
          return thisYearDayOffDates.map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        if (nextYearDayOffDates) {
          return nextYearDayOffDates.map(d => this.$moment(d).format('YYYY-MM-DD'))
        }
        return null
      }
    },
    watch: {
      sessionInfo: {
        deep: true,
        immediate: true,
        handler: function (newVal) {
          this.mapFields(newVal)
        }
      }
    },
    created () {
      this.mapFields(this.session)
    },
    methods: {
      validate: async function () {
        const result = await this.$refs.session.validate()
        if (!result) {
          throw new Vue.errorsTypes.ValidationError()
        }
        if (this.validated) {
          if (!this.showOrganizations) {
            this.session.invitedOrganizations = []
          }
          const session = {
            ...this.session
          }
          await this.validated(session)
        }
      },
      mapFields: function (session) {
        this.session.dateStart = session.dateStart
        this.session.dateEnd = session.dateEnd
        this.session.prolongation = session.prolongation

        this.session.tradeDirectionType = session.tradeDirectionType || TRADE_DIRECTION_TYPES.RAISING
        if (session.startPremiums) {
          this.session.startPremiums = JSON.parse(JSON.stringify(session.startPremiums))
        }
        if (session.invitedOrganizations) {
          this.session.invitedOrganizations = JSON.parse(JSON.stringify(session.invitedOrganizations))
        }
        if (session.invitedOrganizations && session.invitedOrganizations.length) {
          this.showOrganizations = true
        }
      }
    }
  }
</script>
